import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Award, Building, Trophy, Star, Plus, X, Save, Loader2 } from "lucide-react";
import { getAcceleratos, getFundingRounds, getCompanySocialProof, saveCompanyFunding, addCompanyTestimonial, removeCompanyTestimonial, addCompanyAward, removeCompanyAward } from "../../utils/api";

const SocialProofSubmission = () => {
  const [formData, setFormData] = useState({
    acceleratorProgram: "",
    acceleratorProgramOther: "",
    acceleratorYear: "",
    acceleratorBatch: "",
    fundingRound: "",
    fundingAmount: "",
    notableCustomers: [""],
    customerLogos: [""],
    productHuntBadges: [""],
    awards: [""],
    certifications: [""],
    mediaFeatures: [""],
    testimonials: [{ name: "", company: "", title: "", quote: "" }],
    metrics: [{ metric: "", value: "" }],
    additionalInfo: ""
  });

  const [acceleratorPrograms, setAcceleratorPrograms] = useState([]);
  const [fundingRounds, setFundingRounds] = useState([]);
  const [selectedAcceleratorProgram, setSelectedAcceleratorProgram] = useState(null);
  const [selectedFundingRound, setSelectedFundingRound] = useState(null);
  const [acceleratorYear, setAcceleratorYear] = useState("");
  const [acceleratorBatch, setAcceleratorBatch] = useState("");
  const [fundingAmount, setFundingAmount] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialCustomerName, setTestimonialCustomerName] = useState("");
  const [testimonialCompany, setTestimonialCompany] = useState("");
  const [testimonialCustomerTitle, setTestimonialCustomerTitle] = useState("");
  const [testimonialQuote, setTestimonialQuote] = useState("");
  const [awards, setAwards] = useState([]);
  const [awardName, setAwardName] = useState("");
  const [awardDescription, setAwardDescription] = useState("");
  const [awardUrl, setAwardUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [testimonialLoading, setTestimonialLoading] = useState(false);
  const [awardLoading, setAwardLoading] = useState(false);

  async function fetchCompanySocialProof() {
    const response = await getCompanySocialProof();
    if (response.success) {
      const data = response.data;
      const funding = data.funding;
      const testimonials = data.testimonials || [];

      setTestimonials(testimonials);
      setAwards(data.awards || []);

      if (funding != null) {
        setSelectedAcceleratorProgram(funding.accelerator_id || "");
        setSelectedFundingRound(funding.funding_round_id || "");
        setFundingAmount(funding.amount || "");
        setAcceleratorYear(funding.accelerator_year || "");
        setAcceleratorBatch(funding.accelerator_batch || "");
      }

      setLoading(false);
    }
  }

  useEffect(() => {
    async function fetchCompanySocialProof() {
      const response = await getCompanySocialProof();
      if (response.success) {
        const data = response.data;
        const funding = data.funding;
        const testimonials = data.testimonials || [];

        if (funding != null) {
          setSelectedAcceleratorProgram(funding.accelerator_id || "");
          setSelectedFundingRound(funding.funding_round_id || "");
          setFundingAmount(funding.amount || "");
          setAcceleratorYear(funding.accelerator_year || "");
          setAcceleratorBatch(funding.accelerator_batch || "");

          setTestimonials(testimonials);
          setAwards(data.awards || []);
        }

        setLoading(false);
      }
    }

    async function fetchAcceleratorPrograms() {
      const response = await getAcceleratos();
      if (response.success) {
        setAcceleratorPrograms(response.data);
      } else {
        console.error("Failed to fetch accelerator programs:", response.message);
      }
    }

    async function fetchFundingRounds() {
      const response = await getFundingRounds();
      if (response.success) {
        setFundingRounds(response.data);
      } else {
        console.error("Failed to fetch funding rounds:", response.message);
      }
    }
    fetchCompanySocialProof();
    fetchFundingRounds();
    fetchAcceleratorPrograms();
  }, []);

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };


  const handleArrayChange = (field, index, value) => {
    setFormData(prev => {
      const currentArray = prev[field];
      return {
        ...prev,
        [field]: currentArray.map((item, i) =>
          i === index ? value : item
        )
      };
    });
  };

  const handleTestimonialChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      testimonials: prev.testimonials.map((testimonial, i) =>
        i === index ? { ...testimonial, [field]: value } : testimonial
      )
    }));
  };

  const handleMetricChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      metrics: prev.metrics.map((metric, i) =>
        i === index ? { ...metric, [field]: value } : metric
      )
    }));
  };

  const addArrayItem = (field, defaultValue = "") => {
    setFormData(prev => {
      const currentArray = prev[field];
      return {
        ...prev,
        [field]: [...currentArray, defaultValue]
      };
    });
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => {
      const currentArray = prev[field];
      return {
        ...prev,
        [field]: currentArray.filter((_, i) => i !== index)
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Social proof submitted:", formData);
    // toast.success("Social proof and accolades submitted successfully!");
  };

  // Add this new function to handle section saves
  const handleSaveSection = (sectionName) => {
    console.log(`Saving section: ${sectionName}`, formData);
    // Here you would implement the API call to save just this section
    // For example: await saveSocialProofSection(sectionName, formData);

    // Show success feedback
    alert(`${sectionName} section saved successfully!`);
  };

  async function handleSaveFunding() {
    let res = await saveCompanyFunding(selectedAcceleratorProgram, selectedFundingRound, fundingAmount, acceleratorYear, acceleratorBatch);

    if (res.success) {
      alert("Funding information saved successfully!");
    } else {
      console.error("Failed to save funding information:", res.message);
      alert("Failed to save funding information. Please try again.");
    }
  }

  async function handleSaveTestimonial() {
    setTestimonialLoading(true);
    let res = await addCompanyTestimonial(
      testimonialCustomerName,
      testimonialCompany,
      testimonialCustomerTitle,
      testimonialQuote
    );

    if (res.success) {
      // Reset testimonial fields
      setTestimonialCustomerName("");
      setTestimonialCompany("");
      setTestimonialCustomerTitle("");
      setTestimonialQuote("");

      fetchCompanySocialProof(); // Refresh testimonials

    } else {
      console.error("Failed to add testimonial:", res.message);
      alert("Failed to add testimonial. Please try again.");
    }
    setTestimonialLoading(false);
  }

  async function handleRemoveTestimonial(testimonialId) {
    let res = await removeCompanyTestimonial(testimonialId);

    if (res.success) {
      // Remove the testimonial from the state
      setTestimonials(prev => prev.filter(t => t.company_testimonial_id !== testimonialId));
    } else {
      console.error("Failed to remove testimonial:", res.message);
      alert("Failed to remove testimonial. Please try again.");
    }
  }

  async function handleAddAward() {
    if (!awardName) {
      alert("Please enter an award name.");
      return;
    }
    setAwardLoading(true);
    let res = await addCompanyAward(awardName, awardUrl, awardDescription);

    if (res.success) {
      // Reset award fields
      setAwardName("");
      setAwardDescription("");
      setAwardUrl("");

      // Refresh awards
      fetchCompanySocialProof();
    } else {
      console.error("Failed to add award:", res.message);
      alert("Failed to add award. Please try again.");
    }
    setAwardLoading(false);
  }

  async function handleRemoveAward(awardId) {
    let res = await removeCompanyAward(awardId);

    if (res.success) {
      // Remove the award from the state
      setAwards(prev => prev.filter(a => a.company_award_id !== awardId));
    } else {
      console.error("Failed to remove award:", res.message);
      alert("Failed to remove award. Please try again.");
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">


      <div className=" py-4 max-w-4xl">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Showcase Your Social Proof</h2>
          <p className="text-lg text-gray-600">
            Add social proof and accolades to build trust and credibility with potential customers.
            This information will be displayed on your company profile and product pages.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Accelerator & Funding */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Building className="h-5 w-5 text-blue-600" />
                  Accelerator & Funding
                </h3>
                <button
                  type="button"
                  onClick={() => handleSaveFunding()}
                  className="px-3 py-1 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md border border-blue-200 flex items-center gap-1"
                >
                  <Save className="h-4 w-4" />
                  Save
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="">
                  <label className="block text-sm font-medium text-gray-700">Accelerator Program</label>
                  <select
                    name="acceleratorProgram"
                    value={selectedAcceleratorProgram}
                    onChange={(e) => setSelectedAcceleratorProgram(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select accelerator program</option>
                    {acceleratorPrograms.map((program) => (
                      <option key={program.accelerator_id} value={program.accelerator_id}>
                        {program.name}
                      </option>
                    ))}
                  </select>

                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Year</label>
                  <input
                    type="text"
                    name="acceleratorYear"
                    value={acceleratorYear}
                    onChange={(e) => setAcceleratorYear(e.target.value)}
                    placeholder="e.g., 2023"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Batch/Cohort</label>
                  <input
                    type="text"
                    name="acceleratorBatch"
                    value={acceleratorBatch}
                    onChange={(e) => setAcceleratorBatch(e.target.value)}
                    placeholder="e.g., W23, Summer 2023"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Latest Funding Round</label>
                  <select
                    name="fundingRound"
                    value={selectedFundingRound}
                    onChange={(e) => setSelectedFundingRound(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select funding round</option>
                    {fundingRounds.map((round) => (
                      <option key={round.funding_round_id} value={round.funding_round_id}>
                        {round.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Funding Amount</label>
                  <input
                    type="text"
                    name="fundingAmount"
                    value={fundingAmount}
                    onChange={(e) => setFundingAmount(e.target.value)}
                    placeholder="e.g., $2M, $10M"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>


          {/* Testimonials */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Award className="h-5 w-5 text-green-600" />
                  Customer Testimonials
                </h3>
                {/* <button
                  type="button"
                  onClick={() => handleSaveSection('Customer Testimonials')}
                  className="px-3 py-1 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md border border-blue-200 flex items-center gap-1"
                >
                  <Save className="h-4 w-4" />
                  Save
                </button> */}
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {testimonials.map((testimonial, index) => (
                  <div
                    key={index}
                    className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col"
                  >
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100 flex-shrink-0">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-sm">
                          {testimonial.customer_name ? testimonial.customer_name.charAt(0).toUpperCase() : "?"}
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{testimonial.customer_name}</h4>
                          <p className="text-xs text-gray-600">
                            {testimonial.customer_title}
                            {testimonial.customer_title && ", "}
                            {testimonial.company_name}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 flex-grow">
                      <div className="italic text-gray-700 border-l-2 border-indigo-200 pl-3">
                        "{testimonial.quote}"
                      </div>
                    </div>
                    <div className="p-3 border-t border-gray-100 bg-gray-50 flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleRemoveTestimonial(testimonial.company_testimonial_id)}
                        className="px-2 py-1 border border-red-200 rounded text-xs font-medium text-red-600 bg-white hover:bg-red-50 focus:outline-none focus:ring-1 focus:ring-red-500 flex items-center gap-1"
                      >
                        <X className="h-3 w-3" />
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                {testimonials.length === 0 && (
                  <div className="col-span-full p-8 border border-dashed border-gray-300 rounded-lg bg-gray-50 flex flex-col items-center justify-center text-center">
                    <Award className="h-12 w-12 text-gray-400 mb-3" />
                    <h4 className="font-medium text-gray-700 mb-1">No testimonials yet</h4>
                    <p className="text-sm text-gray-500">Add your first customer testimonial below</p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Customer Name</label>
                  <input
                    type="text"
                    value={testimonialCustomerName}
                    onChange={(e) => setTestimonialCustomerName(e.target.value)}
                    placeholder="John Smith"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Company</label>
                  <input
                    type="text"
                    value={testimonialCompany}
                    onChange={(e) => setTestimonialCompany(e.target.value)}
                    placeholder="Acme Corp"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    value={testimonialCustomerTitle}
                    onChange={(e) => setTestimonialCustomerTitle(e.target.value)}
                    placeholder="CEO, CTO, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Quote</label>
                <textarea
                  value={testimonialQuote}
                  onChange={(e) => setTestimonialQuote(e.target.value)}
                  placeholder="What they said about your product..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <button
                type="button"
                onClick={() => handleSaveTestimonial()}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
                disabled={testimonialLoading}
              >
                {testimonialLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                {testimonialLoading ? "Adding..." : "Add Testimonial"}
              </button>
            </div>
          </div>


          {/* Product Hunt & Awards */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-orange-600" />
                  Awards & Recognition
                </h3>

              </div>
            </div>
            <div className="p-6 space-y-4">

              <div>
                <label className="block text-sm font-medium text-gray-700">Awards & Certifications</label>
                {awards.map((award, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 bg-gradient-to-r from-yellow-50 to-white border border-yellow-200 rounded-lg px-4 py-3 mb-2 shadow-sm"
                  >
                    <Trophy className="h-6 w-6 text-yellow-500 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 flex items-center gap-2">
                        {award.award_name}
                        {award.award_url && (
                          <a
                            href={award.award_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-1 text-blue-600 hover:underline flex items-center gap-1"
                            title="View Award"
                          >
                            <ArrowLeft className="h-4 w-4 rotate-180" />
                          </a>
                        )}
                      </div>
                      {/* Optionally, add description or other info here */}
                    </div>
                    <button
                      type="button"
                      onClick={async () => handleRemoveAward(award.company_award_id)}
                      className="ml-2 px-2 py-1 border border-red-200 rounded text-xs font-medium text-red-600 bg-white hover:bg-red-50 focus:outline-none focus:ring-1 focus:ring-red-500 flex items-center gap-1"
                    >
                      <X className="h-3 w-3" />
                      Remove
                    </button>
                  </div>
                ))}

                <div className="flex flex-col md:flex-row gap-2 mt-2">
                  <input
                    type="text"
                    value={awardName}
                    onChange={(e) => setAwardName(e.target.value)}
                    placeholder="e.g., SOC 2 Certified, G2 Leader, Capterra Shortlist"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="text"
                    value={awardUrl}
                    onChange={(e) => setAwardUrl(e.target.value)}
                    placeholder="Award URL (optional)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => handleAddAward()}
                  className="mt-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
                  disabled={awardLoading}
                >
                  {awardLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  {awardLoading ? "Adding..." : "Add Award"}
                </button>
              </div>
            </div>
          </div>


          {/* Notable Customers */}
          {/* <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Star className="h-5 w-5 text-yellow-600" />
                  Notable Customers
                </h3>
                <button
                  type="button"
                  onClick={() => handleSaveSection('Notable Customers')}
                  className="px-3 py-1 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md border border-blue-200 flex items-center gap-1"
                >
                  <Save className="h-4 w-4" />
                  Save
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Customer Names</label>
                {formData.notableCustomers.map((customer, index) => (
                  <div key={index} className="flex gap-2 mt-2">
                    <input
                      type="text"
                      value={customer}
                      onChange={(e) => handleArrayChange('notableCustomers', index, e.target.value)}
                      placeholder="e.g., Microsoft, Spotify, Airbnb"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {formData.notableCustomers.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeArrayItem('notableCustomers', index)}
                        className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('notableCustomers')}
                  className="mt-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Customer
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Customer Logo URLs</label>
                {formData.customerLogos.map((logo, index) => (
                  <div key={index} className="flex gap-2 mt-2">
                    <input
                      type="text"
                      value={logo}
                      onChange={(e) => handleArrayChange('customerLogos', index, e.target.value)}
                      placeholder="URL to customer logo"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {formData.customerLogos.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeArrayItem('customerLogos', index)}
                        className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('customerLogos')}
                  className="mt-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Logo URL
                </button>
              </div>
            </div>
          </div> */}




          {/* Key Metrics */}
          {/* <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Key Metrics & Achievements</h3>
                <button
                  type="button"
                  onClick={() => handleSaveSection('Key Metrics')}
                  className="px-3 py-1 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md border border-blue-200 flex items-center gap-1"
                >
                  <Save className="h-4 w-4" />
                  Save
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {formData.metrics.map((metric, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={metric.metric}
                    onChange={(e) => handleMetricChange(index, 'metric', e.target.value)}
                    placeholder="e.g., Monthly Active Users, Revenue Growth"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="text"
                    value={metric.value}
                    onChange={(e) => handleMetricChange(index, 'value', e.target.value)}
                    placeholder="e.g., 100K+, 300% YoY"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {formData.metrics.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('metrics', index)}
                      className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('metrics', { metric: "", value: "" })}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Metric
              </button>
            </div>
          </div> */}

          {/* Additional Information */}
          {/* <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
                <button
                  type="button"
                  onClick={() => handleSaveSection('Additional Information')}
                  className="px-3 py-1 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md border border-blue-200 flex items-center gap-1"
                >
                  <Save className="h-4 w-4" />
                  Save
                </button>
              </div>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700">Additional Social Proof</label>
              <textarea
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleInputChange}
                placeholder="Any other social proof, media mentions, or accolades you'd like to highlight..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div> */}

          {/* <div className="flex justify-end">
            <button 
              type="submit" 
              className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 font-medium"
            >
              Submit Social Proof
            </button>
          </div> */}
        </form>
      </div>
    </div>
  );
};

export default SocialProofSubmission;